import type {
  ExportAnnotation,
  ExportModel,
  ExportNotesSection,
  ExportSelection,
  ExportSubjectDescriptionOptions,
  Noyau,
} from '@mss_creator/shared';
import { applyColumnOrder, pageGeometry, renderExportHtml } from '@mss_creator/shared';
import pmsLogoHorizontalUrl from '../../../../shared/assets/pms/logo-horizontal.png?url';
import pmsLogoRingUrl from '../../../../shared/assets/pms/logo-ring.png?url';
import type { ExportOptionsState } from './export-customization';
import { EXPORT_FONT_FACE_CSS } from './export-fonts';

/**
 * The PMS marks the renderer draws for `dressing.pmsSignature` (spec R4).
 * `?url` imports for the same reason `export-fonts.ts` uses them: the assets
 * live in `packages/shared/assets` (shared with the backend, which embeds them
 * as base64) and Vite emits them as hashed build assets, so asking Vite for
 * the resolved URL is the only way to get a path correct in both dev and
 * production. The renderer's `pmsLogo*DataUri` fields are plain `string`s that
 * end up in an `<img src>` — a bundled URL works there exactly as the font
 * URLs do, and unlike the backend the browser can actually fetch it.
 */
const PMS_LOGO_HORIZONTAL_URL: string = pmsLogoHorizontalUrl;
const PMS_LOGO_RING_URL: string = pmsLogoRingUrl;

export interface ExportHtmlPreviewProps {
  model: ExportModel;
  /** The route's real selection — what the label columns are reserved from, as on the export request. */
  selection: ExportSelection;
  /** The panel's option blocks, passed whole so the preview and the export request cannot disagree. */
  options: ExportOptionsState;
  subjectDescription: ExportSubjectDescriptionOptions;
  matrixTitle: string;
  companyName: string;
  matrixSujet: string | null;
  matrixContexte: string | null;
  noyau: Noyau;
  /** Raw per-column pixel widths — `matrix.viewState.columnWidths`, under the table's own overrides. */
  columnWidths: Record<string, number>;
  /** Raw per-row pixel heights — `matrix.viewState.rowHeights`, read in `ecran` mode only (decision X5). */
  rowHeights: Record<string, number>;
  /**
   * Every annotation, as the html request carries them: the continuous layout
   * keeps the highlights and drops the ink (decision X8), like the file.
   */
  annotations: readonly ExportAnnotation[];
  /** The « Notes » section the file will end with (issue #19), `null` when the option is off or there is nothing to print. */
  notes?: ExportNotesSection | null;
}

/**
 * The HTML export's preview (issue #12, spec F4): the very document the server
 * writes — the shared renderer's `continuous` layout — in an `<iframe srcDoc>`.
 * The iframe is its own scroll container, so the document's sticky palier
 * headers and row labels hold exactly as they will in the downloaded file.
 *
 * It lays the grid out as the backend's html branch does (decision X9): the
 * model in `table.columnOrder`, the screen widths under `table.columnWidths`.
 * It shows the highlights the file keeps (decision X8), but no annotation or
 * « Aménager » control lives here — those are PDF surfaces.
 */
export function ExportHtmlPreview({
  model,
  selection,
  options,
  subjectDescription,
  matrixTitle,
  companyName,
  matrixSujet,
  matrixContexte,
  noyau,
  columnWidths,
  rowHeights,
  annotations,
  notes = null,
}: ExportHtmlPreviewProps) {
  // The mode survives unticking the activation row (the UI only disables its
  // checkbox), so without the row check `column` mode would reserve a second
  // label column with no activation rows to fill it — the backend's own rule.
  const labelColumnCount =
    selection.activationLevierNames === 'column' && selection.includeActivationRow ? 2 : 1;

  const html = renderExportHtml({
    layout: 'continuous',
    model: applyColumnOrder(model, options.table.columnOrder),
    // The continuous layout renders every model column in model order.
    tiles: [],
    matrixTitle,
    companyName,
    subjectDescription: { ...subjectDescription, sujet: matrixSujet, contexte: matrixContexte },
    activationLevierNames: selection.activationLevierNames,
    labelColumnCount,
    columnWidths: { ...columnWidths, ...options.table.columnWidths },
    rowHeights,
    // `page` is PDF-only on the wire (spec R1): the html document keeps the
    // nominal geometry and font size the backend's html branch renders with.
    geometry: pageGeometry({ format: 'a4', orientation: 'landscape', margins: 'normales' }),
    fontSizePt: 12,
    table: options.table,
    dressing: options.dressing,
    appearance: options.appearance,
    // The browser preview skips the company logo fetch — the server export
    // carries it. The PMS marks are bundled assets it can reach directly.
    companyLogoDataUri: null,
    pmsLogoHorizontalDataUri: PMS_LOGO_HORIZONTAL_URL,
    pmsLogoRingDataUri: PMS_LOGO_RING_URL,
    exportedAt: null,
    matrixUpdatedAt: null,
    noyau,
    fontFaceCss: EXPORT_FONT_FACE_CSS,
    annotations,
    notes,
  });

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <iframe
        title="Aperçu HTML"
        srcDoc={html}
        // The document is static: same-origin keeps the bundled fonts and logos
        // loading, and without `allow-scripts` nothing in it can run.
        sandbox="allow-same-origin"
        className="h-full w-full rounded-lg border border-gray-200 bg-white"
      />
    </div>
  );
}
